/**
 * Shared schema.org JSON-LD entities.
 *
 * JSON-LD validators (and the squirrel crawler) parse each <script> block
 * independently, so a single document-wide @graph with @id cross-references
 * does NOT resolve for them — every top-level block has to be self-contained.
 * We therefore embed the Person entity *inline* as author/publisher wherever
 * it's needed, and define it once here so the facts can't drift.
 */
import { CONTACT_EMAIL, SOCIAL_URLS } from './contact'
import { profile, workHistory } from './mcp/profile'
import { getSiteUrl, SCHOOL, SITE_NAME } from './site'

const SCHEMA_CONTEXT = 'https://schema.org'
const siteUrl = getSiteUrl()

/**
 * The Person entity. Carries its own @context so it stays valid both as a
 * standalone block AND when embedded as author/publisher — validators check
 * every typed node for @context, including nested ones.
 */
export const personEntity = {
  '@context': SCHEMA_CONTEXT,
  '@type': 'Person',
  name: SITE_NAME,
  givenName: 'Arne',
  familyName: 'Noori',
  url: siteUrl,
  // A stable public URL for the headshot: people-search and knowledge panels
  // want a face, and the hashed next/image URL changes every build.
  image: {
    '@type': 'ImageObject',
    url: `${siteUrl}/images/arne-noori.jpg`,
    width: 800,
    height: 800,
  },
  email: `mailto:${CONTACT_EMAIL}`,
  sameAs: [
    SOCIAL_URLS.twitter,
    SOCIAL_URLS.github,
    SOCIAL_URLS.linkedin,
    SOCIAL_URLS.instagram,
    'https://friendship.co',
  ],
  jobTitle: profile.currentRole.title,
  description: profile.headline,
  // People-search indexes key on employer, school, location and skills, so
  // these come from the canonical profile rather than hand-written copy.
  worksFor: [
    { '@type': 'Organization', name: profile.currentRole.company },
    {
      '@type': 'Organization',
      name: 'The Friendship Company',
      url: 'https://friendship.co',
    },
  ],
  hasOccupation: {
    '@type': 'Occupation',
    name: profile.currentRole.title,
    occupationalCategory: '15-1252.00 Software Developers',
    skills: profile.primaryStack.join(', '),
  },
  alumniOf: {
    '@type': 'CollegeOrUniversity',
    name: SCHOOL,
    url: 'https://www.calpoly.edu',
  },
  // profile.location is the MCP fallback, not where Arne lives; home is fixed here.
  homeLocation: {
    '@type': 'Place',
    name: 'Irvine, California',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Irvine',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
  },
  knowsAbout: [
    ...profile.focusAreas,
    ...profile.primaryStack,
    'Machine learning',
    'iOS development',
    'SwiftUI',
  ],
  knowsLanguage: 'en',
  // Past employers, for "who worked at X" style lookups.
  affiliation: workHistory.slice(1).map((r) => ({
    '@type': 'Organization',
    name: r.company,
  })),
} as const

/** The WebSite entity. Carries @context for the same reason (used as isPartOf). */
export const websiteEntity = {
  '@context': SCHEMA_CONTEXT,
  '@type': 'WebSite',
  name: SITE_NAME,
  url: siteUrl,
  description: `Personal website of ${SITE_NAME}: ${profile.headline}`,
  author: { '@type': 'Person', name: SITE_NAME, url: siteUrl },
} as const

/** Standalone Person block for the site <head>. */
export const personSchema = personEntity

/** Standalone WebSite block for the site <head>. Flat — no nested entities. */
export const websiteSchema = websiteEntity

/**
 * ProfilePage for / and /about — Google's supported type for a page that is
 * primarily about one person. Person is inlined (see the header note on why
 * @id references are avoided).
 */
export function profilePageSchema(path: string) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ProfilePage',
    url: `${siteUrl}${path}`,
    mainEntity: personEntity,
    isPartOf: websiteEntity,
  }
}

/** A portfolio project, credited to Arne so project searches resolve to him. */
export function projectSchema(project: {
  name: string
  description: string
  slug: string
  heroImage?: string
  technologies: { name: string }[]
  links: { href: string }[]
}) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'CreativeWork',
    name: project.name,
    description: project.description,
    url: `${siteUrl}/projects/${project.slug}`,
    creator: personEntity,
    author: personEntity,
    ...(project.heroImage && { image: `${siteUrl}${project.heroImage}` }),
    keywords: project.technologies.map((t) => t.name).join(', '),
    sameAs: project.links
      .map((l) => l.href)
      .filter((h) => h.startsWith('http')),
  }
}

/** BreadcrumbList from ordered [name, path] pairs; the home crumb is implied. */
export function breadcrumbSchema(crumbs: [name: string, path: string][]) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: [['Home', ''] as const, ...crumbs].map(
      ([name, path], i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name,
        item: `${siteUrl}${path}`,
      }),
    ),
  }
}

/** /projects index: a CollectionPage listing every project Arne built. */
export function projectsCollectionSchema(
  projects: { name: string; description: string; slug: string }[],
) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'CollectionPage',
    url: `${siteUrl}/projects`,
    name: `Projects by ${SITE_NAME}`,
    author: personEntity,
    isPartOf: websiteEntity,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: projects.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${siteUrl}/projects/${p.slug}`,
        name: p.name,
      })),
    },
  }
}

/** The Friendship Company and the apps it ships, all founded/built by Arne. */
export function friendshipCompanySchema(
  apps: {
    name: string
    href: string
    note: string
    tag: string
    category: string
  }[],
) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Organization',
    name: 'The Friendship Company',
    url: 'https://friendship.co',
    description:
      'Small software for small circles: consumer apps that help close friends stay close.',
    founder: personEntity,
    location: { '@type': 'Place', name: 'Irvine, California' },
    sameAs: [`${siteUrl}/projects/friendship`],
    owns: apps.map((app) => ({
      '@type': 'SoftwareApplication',
      name: app.name,
      url: app.href,
      description: app.note,
      applicationCategory: app.category,
      operatingSystem: app.tag.includes('Web') ? 'iOS, Web' : 'iOS',
      author: { '@type': 'Person', name: SITE_NAME, url: siteUrl },
    })),
  }
}

/** /contact: a ContactPage whose subject is Arne. */
export const contactPageSchema = {
  '@context': SCHEMA_CONTEXT,
  '@type': 'ContactPage',
  url: `${siteUrl}/contact`,
  name: `Contact ${SITE_NAME}`,
  mainEntity: personEntity,
  isPartOf: websiteEntity,
}
